import {
    ChallengePageEntrySupportDto,
    CommunityPageEntrySupportDto,
    TeamSupportDto
} from '@api/ChallengeClient';
import { TenantPageEntrySupportDto } from '@src/core/api/TenantClient';

type CommunityTeamRequirementValidationArgs = {
    selectedTenants: TenantPageEntrySupportDto[];
    selectedCommunities: CommunityPageEntrySupportDto[];
    selectedTeams: TeamSupportDto[];
    selectedChallenges: ChallengePageEntrySupportDto[];
    communityTeamRequired: string[];
};

export type SmartConnectBlockingValidationIssue = {
    blocking: true;
    message: string;
};

export const getNormalizedCommunityTeamRequired = ({
    selectedCommunities,
    communityTeamRequired
}: Pick<
    CommunityTeamRequirementValidationArgs,
    'selectedCommunities' | 'communityTeamRequired'
>): string[] => {
    const selectedInvitationCodes = new Set(
        selectedCommunities
            .map((community) => community.invitationCode)
            .filter(Boolean)
    );

    return Array.from(new Set(communityTeamRequired)).filter((code) =>
        selectedInvitationCodes.has(code)
    );
};

export const getSmartConnectBlockingValidationIssues = ({
    selectedCommunities,
    selectedTeams,
    communityTeamRequired
}: Pick<
    CommunityTeamRequirementValidationArgs,
    'selectedCommunities' | 'selectedTeams' | 'communityTeamRequired'
>): SmartConnectBlockingValidationIssue[] => {
    const requiredCommunitiesById = new Map(
        selectedCommunities
            .filter(
                (community) =>
                    community.invitationCode &&
                    communityTeamRequired.includes(community.invitationCode)
            )
            .map((community) => [community.id, community])
    );

    return selectedTeams.flatMap((team) => {
        const requiredCommunity = team.community?.id
            ? requiredCommunitiesById.get(team.community.id)
            : undefined;
        if (!requiredCommunity) {
            return [];
        }

        return [
            {
                blocking: true as const,
                message:
                    `Team "${team.name}" belongs to community "${requiredCommunity.name}", ` +
                    'so you cannot require team choice and include a direct team deeplink for that community at the same time'
            }
        ];
    });
};

export const buildSmartConnectValidationContext = ({
    selectedTenants,
    selectedCommunities,
    selectedTeams,
    selectedChallenges,
    communityTeamRequired
}: CommunityTeamRequirementValidationArgs) => {
    const normalizedCommunityTeamRequired = getNormalizedCommunityTeamRequired({
        selectedCommunities,
        communityTeamRequired
    });
    const blockingIssues = getSmartConnectBlockingValidationIssues({
        selectedCommunities,
        selectedTeams,
        communityTeamRequired: normalizedCommunityTeamRequired
    });

    return {
        selectedTenants,
        selectedCommunities,
        selectedTeams,
        selectedChallenges,
        communityTeamRequired: normalizedCommunityTeamRequired,
        blockingIssues,
        hasBlockingIssues: blockingIssues.length > 0
    };
};
